import { PageHero } from "../components/PageHero";
import { Section, Card, ButtonLink } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { BrowserFrame } from "../components/BrowserFrame";
import { CTASection } from "../sections/CTASection";
import { CASE_STUDIES, type CaseStudy } from "../data/work";

/**
 * /work
 *
 * One study. There is no "and 40 more projects" strip, no logo wall and no
 * invented metrics, because we cannot evidence any of those and this is the
 * page where a technical buyer goes looking for the seam. Agent Labista was
 * removed when it turned out to have no users: a case study needs an
 * outcome.
 *
 * The four-part structure — problem, approach, technology, outcome — is
 * the one engineers use with each other, and the approach section is
 * deliberately the longest. Anyone can list technologies; the decision and
 * the reason behind it is the only part that shows judgement.
 */

function Study({ study, index }: { study: CaseStudy; index: number }) {
  return (
    <article id={study.slug} className="scroll-mt-24">
      <Card className="overflow-hidden p-6 sm:p-8 lg:p-12">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
          <span className="font-mono tabular-nums text-white/25">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-white/20">/</span>
          <span>{study.product}</span>
          <span className="text-white/20">·</span>
          <span>{study.sector}</span>
          <span className="text-white/20">·</span>
          <span>{study.market}</span>
        </div>

        <h2 className="mt-4 max-w-3xl font-display text-[1.75rem] font-bold leading-[1.12] tracking-tight sm:text-[2.25rem]">
          {study.title}
        </h2>

        <blockquote className="mt-6 border-l-2 border-emerald-400/50 pl-5 font-display text-lg font-medium leading-snug text-white/85 sm:text-xl">
          {study.pullQuote}
        </blockquote>

        {study.shot ? (
          <Reveal delay={0.08} className="mt-8">
            <BrowserFrame
              src={study.shot.src}
              alt={study.shot.alt}
              label={`${study.product} · in production`}
              priority={index === 0}
            />
          </Reveal>
        ) : null}

        <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:gap-12">
          <Part label="Problem" n="01">
            <p>{study.problem}</p>
          </Part>
          <Part label="Approach" n="02">
            <p>{study.approach}</p>
          </Part>
          <Part label="Technology" n="03">
            <ul className="space-y-2">
              {study.technology.map((t) => (
                <li key={t} className="flex gap-2.5">
                  <span className="mt-[9px] size-1 shrink-0 rounded-full bg-emerald-400" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Part>
          <Part label="Outcome" n="04">
            <p>{study.outcome}</p>
            <p className="mt-3 text-xs text-white/35">
              No figures are quoted here. We have not published numbers our
              customers have agreed to, and an unverifiable metric would be
              worth less than the sentence it replaced.
            </p>
          </Part>
        </div>

        <div className="mt-10 border-t border-white/[0.08] pt-8">
          <ButtonLink href={study.href} variant="ghost">
            Explore {study.product}
          </ButtonLink>
        </div>
      </Card>
    </article>
  );
}

function Part({
  label,
  n,
  children,
}: {
  label: string;
  n: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2.5">
        <span className="font-mono text-[11px] tabular-nums text-white/25">{n}</span>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
          {label}
        </h3>
      </div>
      <div className="mt-3 text-sm leading-relaxed text-white/65">{children}</div>
    </div>
  );
}

export function Work() {
  return (
    <>
      <PageHero
        eyebrow="Selected work"
        title={
          <>
            One system in production,{" "}
            <span className="text-gradient">described honestly.</span>
          </>
        }
        lede="It is our own product, because that is the engagement whose details we are free to publish. Client work is covered by the confidentiality you would want us to give you."
        primary={{ href: "/contact", label: "Start a project" }}
        secondary={{ href: "/products", label: "See the products" }}
      />

      <Section tight>
        <div className="container space-y-5">
          {CASE_STUDIES.map((c, i) => (
            <Reveal key={c.slug}>
              <Study study={c} index={i} />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tight>
        <div className="container">
          <Reveal>
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">
                Why there is only one
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/60">
                Because this is the one we can show you end to end — the
                decisions, the architecture and the thing running today —
                without asking anyone's permission or rounding a number in our
                favour. Our other product, Agent Labista, is still in the lab
                and has no users, so it is on the products page rather than
                this one. We would rather you judged us on one you can verify
                than on twelve you cannot. If you want to go deeper, we will
                walk you through the actual system on a call.
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection
        title={
          <>
            Want to be the{" "}
            <span className="text-gradient">next one?</span>
          </>
        }
        lede="Tell us the problem and what a good outcome looks like. If we are the wrong people for it, we will say so early — that is cheaper for both of us than finding out in month three."
        primary={{ href: "/contact", label: "Start a project" }}
        secondary={{ href: "/what-we-do", label: "See how we engage" }}
      />
    </>
  );
}
